// src/AppWrapper.js
import React, { useState, useEffect } from 'react';
import { Globe, Menu, X } from 'lucide-react';
import ComingSoon from './components/ComingSoon';

// Import pages - you'll need to create these as separate files
import HomePage from './pages/HomePage';
import AboutPage from './pages/AboutPage';
import TeamPage from './pages/TeamPage';
import ProjectsPage from './pages/ProjectsPage';
import StrategyPage from './pages/StrategyPage';
import MediaPage from './pages/MediaPage';
import ContactPage from './pages/ContactPage';

const AppWrapper = () => {
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Auto-enable admin mode for localhost development
    const isLocalhost = window.location.hostname === 'localhost' || 
                       window.location.hostname === '127.0.0.1' || 
                       window.location.hostname.includes('localhost');
    
    if (isLocalhost) {
      console.log('🔧 Local development detected - Admin mode enabled automatically');
      setIsAdmin(true);
      setLoading(false);
      return;
    }

    // For production, check admin parameters
    const urlParams = new URLSearchParams(window.location.search);
    const adminParam = urlParams.get('admin');
    const storedAdmin = localStorage.getItem('infinityAdminMode');
    
    if (adminParam === 'true' || storedAdmin === 'true') {
      setIsAdmin(true);
      if (adminParam === 'true') {
        localStorage.setItem('infinityAdminMode', 'true');
        // Clean URL to remove admin parameter
        window.history.replaceState({}, document.title, window.location.pathname);
      }
    }
    
    setLoading(false);
  }, []);

  // Add global admin toggle function
  useEffect(() => {
    window.toggleInfinityAdmin = () => {
      const newAdminState = !isAdmin;
      setIsAdmin(newAdminState);
      localStorage.setItem('infinityAdminMode', newAdminState.toString());
      console.log(`🔧 Infinity Admin Mode: ${newAdminState ? 'ON' : 'OFF'}`);
      if (!newAdminState) {
        window.location.reload(); // Reload to show coming soon page
      }
    };
  }, [isAdmin]);

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-white text-lg">Loading Infinity Development...</div>
      </div>
    );
  }

  // Show coming soon page for regular visitors
  if (!isAdmin) {
    return <ComingSoon />;
  }

  // Show full app for admin with indicator - This is your complete App component
  return (
    <>
      {/* Admin Mode Indicator */}
      <div className="fixed top-4 right-4 z-[200] bg-red-600 text-white px-4 py-2 rounded-full text-sm shadow-xl border-2 border-red-400 backdrop-blur-sm">
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 bg-red-200 rounded-full animate-pulse"></div>
          <span className="font-medium">Admin Mode</span>
          <button 
            onClick={() => window.toggleInfinityAdmin()}
            className="ml-2 text-red-200 hover:text-white transition-colors font-bold"
            title="Exit Admin Mode"
          >
            ✕
          </button>
        </div>
      </div>
      
      {/* Your complete App component code */}
      <InfinityApp />
    </>
  );
};

// Your complete existing App component (renamed to avoid conflicts)
const InfinityApp = () => {
  const [scrollProgress, setScrollProgress] = useState(0);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [language, setLanguage] = useState('en');
  const [currentPage, setCurrentPage] = useState('home');
  
  useEffect(() => {
    const handleScroll = () => {
      const totalScroll = document.documentElement.scrollHeight - window.innerHeight;
      const currentScroll = window.scrollY;
      const progress = (currentScroll / totalScroll) * 100;
      setScrollProgress(progress);
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Scroll to top whenever page changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [currentPage]);
  
  const toggleLanguage = () => {
    setLanguage(language === 'en' ? 'es' : 'en');
  };
  
  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  // Content based on language
  const content = {
    en: {
      nav: {
        home: 'HOME',
        about: 'ABOUT',
        team: 'TEAM',
        projects: 'PROJECTS',
        strategy: 'STRATEGY',
        media: 'MEDIA',
        contact: 'CONTACT'
      },
      home: {
        title: 'Shaping the Future of Cities',
        subtitle: 'Through Sustainable Innovation and Cross-Continental Development',
        cta1: 'View Projects',
        cta2: 'Schedule a Call',
        services: 'Our Focus Areas',
        servicesList: [
          {
            title: 'Multifamily Development',
            description: 'Creating sustainable residential communities across emerging markets with focus on affordability and innovation.'
          },
          {
            title: 'Cross-Border Investment',
            description: 'Bridging capital between US and Latin American markets through strategic partnerships and local expertise.'
          },
          {
            title: 'Sustainable Innovation',
            description: 'Integrating AI and green technologies into the development process for maximum efficiency and impact.'
          },
          {
            title: 'Community Impact',
            description: 'Building projects that revitalize communities and create long-term value for residents and investors.'
          }
        ]
      },
      about: {
        title: 'About Infinity Development',
        overview1: 'Infinity Development is a forward-thinking, bilingual real estate development firm focused on building high-impact, sustainable communities across the Americas. We merge real estate development, entrepreneurship, and emerging technologies to help shape the cities of tomorrow.',
        overview2: 'With a strategic focus on Latin America and U.S. gateway markets like Los Angeles, Nashville, and Houston, we are building a cross-continental platform that drives innovation, capital, and purpose into underserved and emerging markets.',
        missionTitle: 'Our Mission',
        mission: 'To transform ideas into impactful realities through innovative design, sustainable development, and meticulous execution that benefits both communities and investors.',
        visionTitle: 'Our Vision',
        vision: 'To position Infinity Development as a cross-continental platform driving innovation, capital, and purpose into underserved and emerging markets across the Americas.',
        valuesTitle: 'Core Values',
        values: [
          {
            title: 'Innovation',
            description: 'Leveraging cutting-edge technology and AI to revolutionize the development process and create more efficient, sustainable projects.'
          },
          {
            title: 'Sustainability',
            description: 'Committed to environmental responsibility and creating developments that contribute positively to their communities and the planet.'
          },
          {
            title: 'Purpose-Driven Capital',
            description: 'Focusing investments on projects that generate both financial returns and meaningful social impact across communities.'
          }
        ]
      },
      team: {
        title: 'Our Team',
        members: [
          {
            name: 'Zeus Hernandez',
            title: 'Founder & CEO',
            bio: 'Zeus Hernandez is the Founder & CEO of Infinity Development. A third-year student at UCLA, Zeus is merging real estate development, entrepreneurship, and emerging technologies to help shape the cities of tomorrow. With hands-on experience in construction, entitlements, capital planning, and strategic partnerships, Zeus is passionate about integrating AI into the AEC sector and building a cross-continental platform for innovation.',
            linkedin: 'https://www.linkedin.com/in/zeus-hernandez/'
          },
          {
            name: 'DJ [Last Name]',
            title: 'Co-Founder & [Title]',
            bio: 'Bio for DJ coming soon. Key partner in Infinity Development with expertise in [area of focus].',
            linkedin: '#'
          }
        ]
      },
      projects: {
        title: 'Our Projects',
        subtitle: 'Building the future through strategic development and community impact',
        list: [
          {
            title: 'Los Angeles Infill Development',
            description: 'Sourcing and developing strategic infill projects in high-growth Los Angeles neighborhoods with focus on affordability and sustainability.',
            status: 'Sourcing',
            tags: ['Multifamily', 'Infill', 'Sustainable']
          },
          {
            title: 'Fire-Impacted Community Rebuild',
            description: 'Leading public-private initiatives to rebuild and revitalize communities impacted by California wildfires.',
            status: 'Planning',
            tags: ['Community Impact', 'Public-Private', 'Rebuild']
          },
          {
            title: 'Latin America Expansion',
            description: 'Developing cross-border investment opportunities in Mexico and Argentina through strategic local partnerships.',
            status: 'Development',
            tags: ['International', 'Cross-Border', 'Strategic']
          },
          {
            title: 'AI-Enabled Development Platform',
            description: 'Creating technology solutions to streamline the development process and improve efficiency across all projects.',
            status: 'Innovation',
            tags: ['Technology', 'AI', 'Platform']
          }
        ]
      },
      strategy: {
        title: 'Investment Strategy',
        sections: [
          {
            title: 'Our Acquisition Approach',
            content: 'We focus on sourcing and structuring development deals that generate long-term value for investors while revitalizing communities across multiple regions. Our approach emphasizes strategic partnerships, local market knowledge, and innovative financing structures.'
          },
          {
            title: 'Target Markets & Asset Classes',
            content: 'Primary focus on multifamily, industrial, and infill development projects in gateway and emerging markets including Los Angeles, Nashville, Houston, and key Latin American cities. We target projects with strong fundamentals, community impact potential, and technology integration opportunities.'
          },
          {
            title: 'Capital Strategy',
            content: 'Our capital stack strategy combines traditional equity and debt financing with grants, impact investments, and cross-border capital. We leverage our bilingual capabilities and cross-continental network to access diverse funding sources and create unique investment opportunities.'
          },
          {
            title: 'Unique Value Proposition',
            content: 'Our bilingual LATAM bridge, AI-enabled workflows, and impact-focused lens create a differentiated approach to development. We integrate emerging technologies throughout the development process while maintaining focus on community benefit and sustainable returns.'
          }
        ]
      },
      media: {
        title: 'Media & Insights',
        podcastTitle: 'The Infinity One Podcast',
        episodes: [
          {
            title: 'Building Cross-Border Real Estate Opportunities',
            description: 'Exploring investment opportunities between US and Latin American markets',
            date: 'Episode 1',
            link: 'https://open.spotify.com/show/3y5E9EcqKNPonl1YoEzg8O'
          },
          {
            title: 'AI in Architecture, Engineering & Construction',
            description: 'How emerging technologies are transforming the development industry',
            date: 'Episode 2',
            link: 'https://open.spotify.com/show/3y5E9EcqKNPonl1YoEzg8O'
          },
          {
            title: 'Sustainable Development in Emerging Markets',
            description: 'Creating impact through purpose-driven real estate development',
            date: 'Episode 3',
            link: 'https://open.spotify.com/show/3y5E9EcqKNPonl1YoEzg8O'
          },
          {
            title: 'The Future of Urban Development',
            description: 'Vision for building the cities of tomorrow',
            date: 'Episode 4',
            link: 'https://open.spotify.com/show/3y5E9EcqKNPonl1YoEzg8O'
          }
        ],
        listen: 'Listen Now'
      },
      contact: {
        title: 'Get In Touch',
        name: 'Name',
        email: 'Email',
        message: 'Message',
        send: 'Send Message',
        address: 'Los Angeles, CA',
        phone: '(707) 699-8732',
        emailAddress: 'team@infinitydevelopment.com',
        addressTitle: 'Location',
        emailTitle: 'Email',
        phoneTitle: 'Phone'
      },
      footer: {
        navigation: 'Navigation',
        legal: 'Legal',
        connect: 'Connect',
        privacy: 'Privacy Policy',
        terms: 'Terms of Service',
        cookies: 'Cookies',
        rights: '© 2025 Infinity Development. All rights reserved.'
      }
    },
    es: {
      nav: {
        home: 'INICIO',
        about: 'NOSOTROS',
        team: 'EQUIPO',
        projects: 'PROYECTOS',
        strategy: 'ESTRATEGIA',
        media: 'MEDIOS',
        contact: 'CONTACTO'
      },
      home: {
        title: 'Dando Forma al Futuro de las Ciudades',
        subtitle: 'A Través de Innovación Sostenible y Desarrollo Transcontinental',
        cta1: 'Ver Proyectos',
        cta2: 'Agendar Llamada',
        services: 'Nuestras Áreas de Enfoque',
        servicesList: [
          {
            title: 'Desarrollo Multifamiliar',
            description: 'Creando comunidades residenciales sostenibles en mercados emergentes con enfoque en asequibilidad e innovación.'
          },
          {
            title: 'Inversión Transfronteriza',
            description: 'Conectando capital entre mercados estadounidenses y latinoamericanos a través de alianzas estratégicas y experiencia local.'
          },
          {
            title: 'Innovación Sostenible',
            description: 'Integrando IA y tecnologías verdes en el proceso de desarrollo para máxima eficiencia e impacto.'
          },
          {
            title: 'Impacto Comunitario',
            description: 'Construyendo proyectos que revitalizan comunidades y crean valor a largo plazo para residentes e inversionistas.'
          }
        ]
      },
      about: {
        title: 'Sobre Infinity Development',
        overview1: 'Infinity Development es una empresa de desarrollo inmobiliario bilingüe y visionaria enfocada en construir comunidades sostenibles de alto impacto en las Américas. Fusionamos desarrollo inmobiliario, emprendimiento y tecnologías emergentes para ayudar a dar forma a las ciudades del mañana.',
        overview2: 'Con un enfoque estratégico en América Latina y mercados estadounidenses como Los Ángeles, Nashville y Houston, estamos construyendo una plataforma transcontinental que impulsa innovación, capital y propósito en mercados desatendidos y emergentes.',
        missionTitle: 'Nuestra Misión',
        mission: 'Transformar ideas en realidades impactantes a través de diseño innovador, desarrollo sostenible y ejecución meticulosa que beneficie tanto a las comunidades como a los inversionistas.',
        visionTitle: 'Nuestra Visión',
        vision: 'Posicionar a Infinity Development como una plataforma transcontinental que impulse innovación, capital y propósito en mercados desatendidos y emergentes de las Américas.',
        valuesTitle: 'Valores Fundamentales',
        values: [
          {
            title: 'Innovación',
            description: 'Aprovechando tecnología de vanguardia e IA para revolucionar el proceso de desarrollo y crear proyectos más eficientes y sostenibles.'
          },
          {
            title: 'Sostenibilidad',
            description: 'Comprometidos con la responsabilidad ambiental y la creación de desarrollos que contribuyan positivamente a sus comunidades y al planeta.'
          },
          {
            title: 'Capital con Propósito',
            description: 'Enfocando inversiones en proyectos que generen tanto retornos financieros como impacto social significativo en las comunidades.'
          }
        ]
      },
      team: {
        title: 'Nuestro Equipo',
        members: [
          {
            name: 'Zeus Hernandez',
            title: 'Fundador y CEO',
            bio: 'Zeus Hernandez es el Fundador y CEO de Infinity Development. Estudiante de tercer año en UCLA, Zeus está fusionando desarrollo inmobiliario, emprendimiento y tecnologías emergentes para ayudar a dar forma a las ciudades del mañana. Con experiencia práctica en construcción, permisos, planificación de capital y alianzas estratégicas, Zeus es apasionado por integrar IA en el sector AEC y construir una plataforma transcontinental para la innovación.',
            linkedin: 'https://www.linkedin.com/in/zeus-hernandez/'
          },
          {
            name: 'DJ [Apellido]',
            title: 'Co-Fundador y [Título]',
            bio: 'Biografía de DJ próximamente. Socio clave en Infinity Development con experiencia en [área de enfoque].',
            linkedin: '#'
          }
        ]
      },
      projects: {
        title: 'Nuestros Proyectos',
        subtitle: 'Construyendo el futuro a través de desarrollo estratégico e impacto comunitario',
        list: [
          {
            title: 'Desarrollo de Relleno en Los Ángeles',
            description: 'Búsqueda y desarrollo de proyectos de relleno estratégicos en vecindarios de Los Ángeles de alto crecimiento con enfoque en asequibilidad y sostenibilidad.',
            status: 'Búsqueda',
            tags: ['Multifamiliar', 'Relleno', 'Sostenible']
          },
          {
            title: 'Reconstrucción de Comunidades Afectadas por Incendios',
            description: 'Liderando iniciativas público-privadas para reconstruir y revitalizar comunidades impactadas por incendios forestales de California.',
            status: 'Planificación',
            tags: ['Impacto Comunitario', 'Público-Privado', 'Reconstrucción']
          },
          {
            title: 'Expansión en América Latina',
            description: 'Desarrollando oportunidades de inversión transfronteriza en México y Argentina a través de alianzas locales estratégicas.',
            status: 'Desarrollo',
            tags: ['Internacional', 'Transfronterizo', 'Estratégico']
          },
          {
            title: 'Plataforma de Desarrollo Habilitada por IA',
            description: 'Creando soluciones tecnológicas para agilizar el proceso de desarrollo y mejorar la eficiencia en todos los proyectos.',
            status: 'Innovación',
            tags: ['Tecnología', 'IA', 'Plataforma']
          }
        ]
      },
      strategy: {
        title: 'Estrategia de Inversión',
        sections: [
          {
            title: 'Nuestro Enfoque de Adquisición',
            content: 'Nos enfocamos en buscar y estructurar acuerdos de desarrollo que generen valor a largo plazo para los inversionistas mientras revitalizan comunidades en múltiples regiones. Nuestro enfoque enfatiza alianzas estratégicas, conocimiento del mercado local y estructuras de financiamiento innovadoras.'
          },
          {
            title: 'Mercados Objetivo y Clases de Activos',
            content: 'Enfoque principal en proyectos de desarrollo multifamiliar, industrial y de relleno en mercados principales y emergentes incluyendo Los Ángeles, Nashville, Houston y ciudades clave de América Latina. Nos dirigimos a proyectos con fundamentos sólidos, potencial de impacto comunitario y oportunidades de integración tecnológica.'
          },
          {
            title: 'Estrategia de Capital',
            content: 'Nuestra estrategia de estructura de capital combina financiamiento tradicional de equity y deuda con subvenciones, inversiones de impacto y capital transfronterizo. Aprovechamos nuestras capacidades bilingües y red transcontinental para acceder a fuentes diversas de financiamiento y crear oportunidades de inversión únicas.'
          },
          {
            title: 'Propuesta de Valor Única',
            content: 'Nuestro puente bilingüe LATAM, flujos de trabajo habilitados por IA y enfoque de impacto crean un enfoque diferenciado para el desarrollo. Integramos tecnologías emergentes a lo largo del proceso de desarrollo mientras mantenemos el enfoque en el beneficio comunitario y retornos sostenibles.'
          }
        ]
      },
      media: {
        title: 'Medios e Insights',
        podcastTitle: 'The Infinity One Podcast',
        episodes: [
          {
            title: 'Construyendo Oportunidades Inmobiliarias Transfronterizas',
            description: 'Explorando oportunidades de inversión entre mercados estadounidenses y latinoamericanos',
            date: 'Episodio 1',
            link: 'https://open.spotify.com/show/3y5E9EcqKNPonl1YoEzg8O'
          },
          {
            title: 'IA en Arquitectura, Ingeniería y Construcción',
            description: 'Cómo las tecnologías emergentes están transformando la industria del desarrollo',
            date: 'Episodio 2',
            link: 'https://open.spotify.com/show/3y5E9EcqKNPonl1YoEzg8O'
          },
          {
            title: 'Desarrollo Sostenible en Mercados Emergentes',
            description: 'Creando impacto a través del desarrollo inmobiliario con propósito',
            date: 'Episodio 3',
            link: 'https://open.spotify.com/show/3y5E9EcqKNPonl1YoEzg8O'
          },
          {
            title: 'El Futuro del Desarrollo Urbano',
            description: 'Visión para construir las ciudades del mañana',
            date: 'Episodio 4',
            link: 'https://open.spotify.com/show/3y5E9EcqKNPonl1YoEzg8O'
          }
        ],
        listen: 'Escuchar Ahora'
      },
      contact: {
        title: 'Contáctanos',
        name: 'Nombre',
        email: 'Correo Electrónico',
        message: 'Mensaje',
        send: 'Enviar Mensaje',
        address: 'Los Ángeles, CA',
        phone: '(707) 699-8732',
        emailAddress: 'team@infinitydevelopment.com',
        addressTitle: 'Ubicación',
        emailTitle: 'Correo',
        phoneTitle: 'Teléfono'
      },
      footer: {
        navigation: 'Navegación',
        legal: 'Legal',
        connect: 'Conectar',
        privacy: 'Política de Privacidad',
        terms: 'Términos de Servicio',
        cookies: 'Cookies',
        rights: '© 2025 Infinity Development. Todos los derechos reservados.'
      }
    }
  };

  // Navigation component
  const Navigation = () => (
    <header className="fixed top-0 left-0 w-full z-50 bg-black bg-opacity-80 backdrop-blur-sm">
      <div className="flex justify-between items-center py-4 px-6 md:px-12">
        <div className="text-white text-xl font-bold">Infinity Development</div>
        
        {/* Desktop Navigation */}
        <nav className="hidden md:flex items-center space-x-8">
          <button 
            onClick={() => setCurrentPage('home')}
            className={`text-sm uppercase tracking-wider transition-colors ${currentPage === 'home' ? 'text-white' : 'text-gray-400 hover:text-white'}`}
          >
            {content[language].nav.home}
          </button>
          <button 
            onClick={() => setCurrentPage('about')}
            className={`text-sm uppercase tracking-wider transition-colors ${currentPage === 'about' ? 'text-white' : 'text-gray-400 hover:text-white'}`}
          >
            {content[language].nav.about}
          </button>
          <button 
            onClick={() => setCurrentPage('team')}
            className={`text-sm uppercase tracking-wider transition-colors ${currentPage === 'team' ? 'text-white' : 'text-gray-400 hover:text-white'}`}
          >
            {content[language].nav.team}
          </button>
          <button 
            onClick={() => setCurrentPage('projects')}
            className={`text-sm uppercase tracking-wider transition-colors ${currentPage === 'projects' ? 'text-white' : 'text-gray-400 hover:text-white'}`}
          >
            {content[language].nav.projects}
          </button>
          <button 
            onClick={() => setCurrentPage('strategy')}
            className={`text-sm uppercase tracking-wider transition-colors ${currentPage === 'strategy' ? 'text-white' : 'text-gray-400 hover:text-white'}`}
          >
            {content[language].nav.strategy}
          </button>
          <button 
            onClick={() => setCurrentPage('media')}
            className={`text-sm uppercase tracking-wider transition-colors ${currentPage === 'media' ? 'text-white' : 'text-gray-400 hover:text-white'}`}
          >
            {content[language].nav.media}
          </button>
          <button 
            onClick={() => setCurrentPage('contact')}
            className={`text-sm uppercase tracking-wider transition-colors ${currentPage === 'contact' ? 'text-white' : 'text-gray-400 hover:text-white'}`}
          >
            {content[language].nav.contact}
          </button>
          <button 
            onClick={toggleLanguage}
            className="ml-4 flex items-center text-gray-400 hover:text-white transition-colors"
          >
            <Globe size={16} className="mr-1" />
            <span className="text-xs uppercase">{language === 'en' ? 'ES' : 'EN'}</span>
          </button>
        </nav>
        
        {/* Mobile menu button */}
        <button className="md:hidden text-white" onClick={toggleMenu}>
          {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>
      
      {/* Progress bar */}
      <div className="h-1 bg-gray-800 w-full">
        <div 
          className="h-full bg-white transition-all duration-300 ease-out"
          style={{ width: `${scrollProgress}%` }}
        />
      </div>
      
      {/* Mobile menu */}
      {isMenuOpen && (
        <div className="md:hidden bg-black text-white p-6 space-y-6">
          {Object.keys(content[language].nav).map((key) => (
            <button 
              key={key}
              onClick={() => {
                setCurrentPage(key);
                setIsMenuOpen(false);
              }}
              className="block w-full text-left py-2 border-b border-gray-800 hover:text-gray-300 transition-colors"
            >
              {content[language].nav[key]}
            </button>
          ))}
          <button 
            onClick={toggleLanguage}
            className="flex items-center py-2 hover:text-gray-300 transition-colors"
          >
            <Globe size={16} className="mr-2" />
            {language === 'en' ? 'Español' : 'English'}
          </button>
        </div>
      )}
    </header>
  );

  // Footer component
  const Footer = () => (
    <footer className="bg-black text-white border-t border-gray-800">
      <div className="max-w-6xl mx-auto px-6 md:px-12 py-12">
        <div className="grid md:grid-cols-4 gap-8">
          <div>
            <div className="text-xl font-bold mb-4">Infinity Development</div>
            <p className="text-gray-400">Shaping the future of cities through sustainable innovation and cross-continental development.</p>
          </div>
          <div>
            <h3 className="font-medium mb-4">{content[language].footer?.navigation || 'Navigation'}</h3>
            <ul className="space-y-2 text-gray-400">
              {Object.keys(content[language].nav).map((key) => (
                <li key={key}>
                  <button 
                    onClick={() => setCurrentPage(key)} 
                    className="hover:text-white transition-colors"
                  >
                    {content[language].nav[key]}
                  </button>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h3 className="font-medium mb-4">{content[language].footer?.legal || 'Legal'}</h3>
            <ul className="space-y-2 text-gray-400">
              <li><button className="hover:text-white transition-colors">{content[language].footer?.privacy || 'Privacy Policy'}</button></li>
              <li><button className="hover:text-white transition-colors">{content[language].footer?.terms || 'Terms of Service'}</button></li>
              <li><button className="hover:text-white transition-colors">{content[language].footer?.cookies || 'Cookies'}</button></li>
            </ul>
          </div>
          <div>
            <h3 className="font-medium mb-4">{content[language].footer?.connect || 'Connect'}</h3>
            <div className="grid grid-cols-3 gap-3">
              {/* Social Media Icons */}
              <a 
                href="https://www.instagram.com/thezeushernandez/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-gray-700 transition-colors"
                aria-label="Instagram"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="2" y="2" width="20" height="20" rx="5" ry="5"></rect>
                  <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"></path>
                  <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"></line>
                </svg>
              </a>
              
              <a 
                href="https://www.linkedin.com/in/zeus-hernandez/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-gray-700 transition-colors"
                aria-label="LinkedIn"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z"></path>
                  <rect x="2" y="9" width="4" height="12"></rect>
                  <circle cx="4" cy="4" r="2"></circle>
                </svg>
              </a>
              
              <a 
                href="https://open.spotify.com/show/3y5E9EcqKNPonl1YoEzg8O" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-gray-700 transition-colors"
                aria-label="Spotify"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"></circle>
                  <path d="M8 14.5c2.5-1 5.5-1 8 0"></path>
                  <path d="M6.5 12c3.5-1 7.5-1 11 0"></path>
                  <path d="M5 9.5c4.5-1 9.5-1 14 0"></path>
                </svg>
              </a>
            </div>
          </div>
        </div>
        <div className="mt-12 pt-6 border-t border-gray-800 text-center text-gray-400 text-sm">
          {content[language].footer?.rights || `© ${new Date().getFullYear()} Infinity Development. All rights reserved.`}
        </div>
      </div>
    </footer>
  );
  
  // Set global styles
  useEffect(() => {
    document.body.style.backgroundColor = '#000';
    document.body.style.color = '#fff';
    document.body.style.margin = '0';
    document.body.style.fontFamily = "'Inter', sans-serif";
    
    return () => {
      document.body.style.backgroundColor = '';
      document.body.style.color = '';
      document.body.style.margin = '';
      document.body.style.fontFamily = '';
    };
  }, []);
  
  return (
    <div className="min-h-screen bg-black text-white">
      <Navigation />
      
      {/* Render the appropriate page */}
      {currentPage === 'home' && <HomePage content={content} language={language} setCurrentPage={setCurrentPage} />}
      {currentPage === 'about' && <AboutPage content={content} language={language} />}
      {currentPage === 'team' && <TeamPage content={content} language={language} />}
      {currentPage === 'projects' && <ProjectsPage content={content} language={language} />}
      {currentPage === 'strategy' && <StrategyPage content={content} language={language} />}
      {currentPage === 'media' && <MediaPage content={content} language={language} />}
      {currentPage === 'contact' && <ContactPage content={content} language={language} />}
      
      <Footer />
      
      {/* Entry animation */}
      <div className="fixed inset-0 bg-black z-[100] animate-fade-out pointer-events-none"></div>
      
      {/* Global Styles */}
      <style jsx>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        
        @keyframes fadeOut {
          from { opacity: 1; }
          to { opacity: 0; }
        }
        
        .animate-fade-in {
          animation: fadeIn 1s ease-out forwards;
        }
        
        .animate-fade-out {
          animation: fadeOut 1.5s forwards;
        }
      `}</style>
    </div>
  );
};

export default AppWrapper;